// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getNotifyStyle } from "@awc/App/Components/Notify";
import xProductPartNumbersStyles from "@awc/App/SharedStyles/ProductPartNumbersStyles";
import { TooltipPresentationProps } from "@awc/MobiusOverrides/Tooltip/Tooltip";
import { ProductPartNumbersStyles } from "@awc/Overrides/Components/ProductPartNumbers";
import { CartLineNotesStyles } from "@awc/Overrides/Widgets/Cart/CartLineNotes";
import AddToList from "@awc/XDL/Icons/AddToList";
import IconTrash from "@awc/XDL/Icons/Close";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { color, createLinkIconStyles, ellipsis, xScreen } from "@awc/XDL/Utilities";
import {
    ProductAvailabilityStyles
} from "@insite/content-library/Components/ProductAvailability";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface CartLineSharedStyles {
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    addToListLink?: LinkPresentationProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    quantityTextField?: TextFieldPresentationProps;
    qtyLabel?: TypographyProps;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeValueText?: TypographyPresentationProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionsContainer?: InjectableCss;
    promotionNameLabel?: TypographyPresentationProps;
    promotionNameText?: InjectableCss;
    removeClickable?: ClickablePresentationProps;
    removeCartLineIcon?: IconPresentationProps;
    cartLineNotes?: CartLineNotesStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    errorMessage?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    configurationOptionText?: TypographyProps;
    configurationOptionLabel?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessageText?: TypographyProps;
    quoteMessageTooltip?: TooltipPresentationProps;
    extendedUnitNetPriceWrapper?: InjectableCss;
}

const sharedPriceText: TypographyProps = {
    size: fontConfig.fontSize.regular,
    weight: fontConfig.weight.bold,
    lineHeight: fontConfig.lineHeight.singleLineRegular,
    css: css`
        letter-spacing: 0;
    `,
};

const cartLineSharedStyles: CartLineSharedStyles = {
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 114px;
                height: 114px;
                background-color: ${color("common.white")};
                display: flex;
                justify-content: center;
                ${xScreen("lte", "sm")} {
                    width: 270px;
                    height: 270px;
                }
                @media print {
                    width: 120px;
                    height: 120px;
                }
            `,
        },
        img: {
            css: css`
                max-width: 100%;
                max-height: 100%;
                height: auto;
                object-fit: contain;
            `,
        },
    },
    productDescription: {
        productDetailLink: {
            typographyProps: {
                variant: "h3",
                size: fontConfig.fontSize.link,
                forwardAs: "p",
                css: css`
                    margin-bottom: 0;
                    ${ellipsis(1)}
                `,
            },
            css: css`
                margin-bottom: 0;
                align-self: baseline;
                width: auto;
                @media print {
                    display: inline-block;
                    width: 80%;
                }
            `,
        },
        productDetailDescription: {
            variant: "h3",
            forwardAs: "p",
            size: fontConfig.fontSize.link,
            css: css`
                margin-bottom: 0;
                ${ellipsis(1)}
            `,
        },
    },
    productPartNumbers: {
        container: {
            css: css`
                ${xScreen("lte", "md")} {
                    > div {
                        gap: 0 22px;
                    }
                }
                @media print {
                    display: inline-block;
                    width: 80%;
                }
            `,
        },
        erpNumberGridItem: {
            css: css`
                flex-basis: auto;
                word-break: keep-all;
            `,
        },
        productNameValueText: {
            css: css`
                margin-top: 10px;
            `,
        },
    },
    addToListLink: {
        ...createLinkIconStyles({
            icon: AddToList,
            linkStyles: {
                typographyProps: {
                    weight: fontConfig.weight.regular,
                },
                icon: {
                    position: "left",
                    iconProps: {
                        size: 13,
                        css: css`
                            &,
                            svg {
                                width: 11px;
                                margin-bottom: 1px;
                            }
                        `,
                    },
                },
            },
            sizeVariant: "medium",
        }),
    },

    productPrice: {
        priceLabelText: {
            size: 12,
            weight: "normal",
            css: css`
                line-height: 13px;
                text-transform: capitalize;
                padding: 0;
                margin-bottom: 4px;
                display: none;
                ${xScreen("lte", "md")} {
                    display: block;
                }
                @media print {
                    display: block;
                    &&& {
                        font-size: 14px;
                        line-height: 16px;
                        margin-bottom: 0;
                    }
                }
            `,
        },
        priceWrapper: {
            css: css`
                display: flex;
                align-items: baseline;
            `,
        },
        price: {
            priceText: {
                ...sharedPriceText,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: fontConfig.fontSize.regular,
                    weight: fontConfig.weight.regular,
                    lineHeight: fontConfig.lineHeight.singleLineRegular,
                },
            },
            errorText: {
                size: fontConfig.fontSize.regular,
                weight: fontConfig.weight.bold,
                lineHeight: fontConfig.lineHeight.singleLineRegular,
            },
        },
        quoteMessage: {
            text: {
                ...sharedPriceText,
            },
        },
    },
    extendedUnitNetPriceText: {
        extendedStyles: {
            heading: {
                size: fontConfig.fontSize.regular,
                weight: fontConfig.weight.regular,
                lineHeight: fontConfig.lineHeight.singleLineRegular,
                css: css`
                    text-transform: capitalize;
                    padding: 0;
                    margin-bottom: 4px;
                    display: none;
                    @media print {
                        display: block;
                        &&& {
                            font-size: 14px;
                            line-height: 16px;
                        }
                    }
                `,
            },
            text: {
                ...sharedPriceText,
            },
            wrapper: {
                css: css`
                    width: fit-content;
                `,
            },
        },
    },
    costCodeGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    costCodeLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    costCodeValueText: {
        ...xProductPartNumbersStyles.value,
    },
    configurationOptionText: {
        ...xProductPartNumbersStyles.value,
    },
    configurationOptionLabel: {
        ...xProductPartNumbersStyles.label,
    },
    promotionsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    promotionNameLabel: {
        ...xProductPartNumbersStyles.label,
    },
    promotionNameText: {
        ...xProductPartNumbersStyles.value,
    },
    qtyLabel: {
        as: "label",
        size: fontConfig.fontSize.small,
        weight: fontConfig.weight.bold,
        lineHeight: fontConfig.lineHeight.singleLineSmall,
        css: css`
            display: block;
            &:after {
                content: ":";
            }
            margin-bottom: 4px;

            @media print {
                &&& {
                    font-size: 14px;
                    line-height: 16px;
                }
            }
            ${labelOnLeft => {
                if (labelOnLeft) {
                    return css`
                        display: inline-block;
                        margin-bottom: 0;
                        margin-right: 12px;
                    `;
                }
            }}
        `,
    },
    removeClickable: {
        css: css`
            justify-content: center;
            width: 36px;
            height: 36px;
            background: ${color("common.ui2")};
        `,
    },
    removeCartLineIcon: {
        src: IconTrash,
        size: 11,
        color: "common.ui4",
        css: css`
            &:hover {
                color: ${color("primary.main")};
            }
        `,
    },
    cartLineNotes: {
        wrapper: {
            css: css`
                width: calc(100% + 24px);
                margin: 0 -12px -12px;
                && {
                    max-width: unset;
                }
            `,
        },
        label: {
            size: fontConfig.fontSize.small,
            lineHeight: fontConfig.lineHeight.singleLineSmall,
            weight: fontConfig.weight.bold,
            css: css`
                background: ${color("common.ui2")};
                padding: 10px 18px;
                display: inline-block;
                button {
                    font-size: ${fontConfig.fontSize.small}px;
                    line-height: ${fontConfig.lineHeight.singleLineSmall};
                    font-weight: ${fontConfig.weight.bold};
                    color: ${color("text.main")};
                }
            `,
        },
        textFieldWrapper: {
            css: css`
                background: ${color("common.ui2")};
                padding: 12px;
                position: relative;
                border-top: 1px solid ${color("common.border")};
            `,
        },
        editableNotesTextField: {
            mergeCss: true,
            cssOverrides: {
                formField: css`
                    label {
                        font-size: ${fontConfig.fontSize.small}px;
                        line-height: ${fontConfig.lineHeight.singleLineSmall};
                        font-weight: ${fontConfig.weight.medium};
                    }
                    label:after {
                        content: ":";
                    }
                `,
                formInputWrapper: css`
                    background: ${color("common.ui2")};
                `,
            },
            css: css`
                &&&& {
                }
            `,
        },
        readOnlyNotesText: {
            wrapper: {
                css: css`
                    display: flex;
                    flex-direction: row;
                `,
            },
            heading: {
                weight: fontConfig.weight.bold,
                lineHeight: fontConfig.lineHeight.singleLineRegular,
                size: fontConfig.fontSize.regular,
                transform: "capitalize",
                color: "secondary",
                css: css`
                    &:after {
                        content: ":";
                        margin-right: 4px;
                    }
                `,
            },
            text: {
                weight: fontConfig.weight.regular,
                lineHeight: fontConfig.lineHeight.singleLineRegular,
                size: fontConfig.fontSize.regular,
                css: css`
                    width: auto;
                `,
            },
        },
    },
    cartLineErrorMessageGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            ${xScreen("lte", "md")} {
                margin-top: 10px;
            }
        `,
    },
    errorMessage: {
        ...getNotifyStyle("danger"),
    },
    productAvailability: {
        messageGridItem: {
            css: css`
                flex-direction: column;
                margin-bottom: 4px;
            `,
        },
    },
    unitOfMeasure: {
        size: 12,
        weight: "normal",
        css: css`
            line-height: 13px;
            text-transform: capitalize;
        `,
    },
    quoteMessageWrapper: {
        css: css`
            display: flex;
            gap: 8px;
            align-items: baseline;
        `,
    },
    quoteMessageText: {
        ...sharedPriceText,
    },
    quoteMessageTooltip: {
        iconProps: {
            height: 16,
            width: 16,
        } as IconProps,
    },

    extendedUnitNetPriceWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            ${xScreen("lte", "md")} {
                justify-content: flex-start;
            }
            ${xScreen("lte", "sm")} {
                align-items: end;
            }
        `,
    },
};

export default cartLineSharedStyles;
